import supabaseClient from '@/api/supabaseClient.ts';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { IntentCaptureRequest, IntentCaptureResponse } from '$/requests/IntentCapture.ts';
import { BackendErrorResponse } from '@/types/response/BackendErrorResponse';
import { buildIntentCapture, type CommonIntakeCaptureSnapshot } from '@/pages/commonIntake/buildIntentCaptureSteps.ts';

export class InsufficientCreditsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InsufficientCreditsError';
  }
}

const useIntentCapture = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (snapshot: CommonIntakeCaptureSnapshot) => submitIntake(snapshot),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['credits'] });
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
    },
  });
};

export default useIntentCapture;

async function submitIntake(snapshot: CommonIntakeCaptureSnapshot): Promise<IntentCaptureResponse> {
  const body = buildIntentCapture(snapshot);
  return invokeIntentCapture<IntentCaptureResponse>(body);
}

async function invokeIntentCapture<T>(body: IntentCaptureRequest): Promise<T> {
  const { data, error } = await supabaseClient.functions.invoke('intent-capture', { body });

  if (error && error instanceof FunctionsHttpError) {
    const errorResponse: BackendErrorResponse = await error.context.json();
    if (errorResponse.status === 402) {
      throw new InsufficientCreditsError(errorResponse.errors.join(', '));
    }
    throw new Error(errorResponse.errors.join(', '));
  }

  return data.data as T;
}
